import { Head, router } from '@inertiajs/react';
import { LoaderCircle } from 'lucide-react';
import { useState } from 'react';

import LoginController from '@/actions/App/Http/Controllers/Organizer/Auth/LoginController';
import RegisterController from '@/actions/App/Http/Controllers/Organizer/Auth/RegisterController';
import AuthLayout from '@/components/ctv/layouts/auth-layout';
import { FormCheckbox } from '@/components/form-input/form-checkbox';
import { FormInput } from '@/components/form-input/form-input';
import TextLink from '@/components/text-link';
import { Button } from '@/components/ui/button';
import { Form } from '@/components/ui/form';
import { post } from '@/lib/axios';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm as useHookForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';

type LoginForm = {
  mobile: string;
  password: string;
  remember?: boolean;
};

interface LoginProps {
  status?: string;
  canResetPassword: boolean;
  pageTitle: string;
  bgImage: string;
}

const formSchema = z.object({
  mobile: z.string().trim().min(1, { message: 'Số điện thoại là bắt buộc' }),
  password: z.string().min(1, { message: 'Mật khẩu là bắt buộc' }),
  remember: z.boolean().optional(),
});

export default function Login({ status, pageTitle, bgImage }: LoginProps) {
  const [processing, setProcessing] = useState(false);
  const form = useHookForm<LoginForm>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      mobile: '',
      password: '',
      remember: false,
    },
  });

  const handleSubmit = async (data: LoginForm) => {
    setProcessing(true);

    try {
      const response = await post(LoginController.loginInertia().url, data);

      if (response?.data?.success) {
        toast.success('Đăng nhập thành công');
        form.reset();
        // Full navigation: session was regenerated, CSRF token is stale.
        window.location.href = response.data.redirect_url ?? '/ctv';
        return;
      }

      // Resolved but failed (axios validateStatus accepts 4xx).
      const failure = response as unknown as {
        error?: string;
        message?: string;
        errors?: Record<string, string | string[]>;
      };

      // Map server-side field errors onto the form.
      if (failure.errors) {
        Object.entries(failure.errors).forEach(([field, msgs]) => {
          form.setError(field as keyof LoginForm, {
            type: 'server',
            message: Array.isArray(msgs) ? msgs[0] : msgs,
          });
        });
      }

      toast.error(failure.error ?? failure.message ?? 'Số điện thoại hoặc mật khẩu không đúng');
    } catch (error: unknown) {
      // Only 5xx / network errors reach here.
      const err = error as { message?: string };
      toast.error(err?.message ?? 'Có lỗi xảy ra khi đăng nhập');
    } finally {
      setProcessing(false);
    }
  };

  return (
    <AuthLayout title={pageTitle} description="Nhập số điện thoại và mật khẩu để đăng nhập" bgImage={bgImage}>
      <Head title={pageTitle} />

      <Form {...form}>
        <form className="flex flex-col gap-6" onSubmit={form.handleSubmit(handleSubmit)}>
          <div className="grid gap-6">
            <div className="grid gap-2">
              <FormInput form={form} type="tel" name="mobile" label="Số điện thoại" placeholder="Số điện thoại" isRequired id="mobile" />
            </div>

            <div className="grid gap-2">
              <FormInput form={form} type="password" name="password" label="Mật khẩu" placeholder="Mật khẩu" isRequired id="password" />
            </div>

            <div className="flex items-center space-x-3">
              <FormCheckbox className="flex items-center" form={form} name="remember" label="Ghi nhớ đăng nhập" id="remember" />
            </div>

            <Button type="submit" className="mt-4 w-full" tabIndex={4} disabled={processing}>
              {processing && <LoaderCircle className="h-4 w-4 animate-spin" />}
              Đăng nhập
            </Button>
          </div>

          <div className="text-muted-foreground text-right text-sm">
            Chưa có tài khoản?{' '}
            <TextLink className="font-semibold" href={RegisterController.showRegistrationFormInertia().url} tabIndex={5}>
              Tạo tài khoản
            </TextLink>
          </div>
        </form>

        {status && <div className="mb-4 text-center text-sm font-medium text-green-600">{status}</div>}
      </Form>
    </AuthLayout>
  );
}
